<template lang="pug">
  .school-tab(v-if='schoolEdition')
    .section.row.school-section(v-if='isAdmin')
      h4.text-big.dark-gray
        | {{ $t('profile.schoolTab.school') }}
      k-select.input.text-normal(
        name='school'
        keyName='value'
        :preselectedOption='schoolEdition.id'
        :options='getSchoolsArray'
        withBorder edition
        @selectOption='getSchoolInfo')
    .gray-section
      .school-header.row.middle.relative
        .school-image-container.relative
          img.school-image(:src='schoolEdition.avatar || defaultSchool' :alt='schoolEdition.name')
          .row.center.middle.width-100.absolute.img-buttons-container
            .column
              input.hidden-input(type='file' id='image-input' @change='handleSubmitImage' accept="image/png,image/jpg,image/jpeg")
              label.hand-cursor.m-right-3(for='image-input')
                simple-svg.dark-gray-hover-blue(
                  :filepath='editIcon'
                  :width='"32px"'
                  :height='"32px"'
                  v-tooltip.bottom='{ content: $t("profile.changeAvatar"), classes: "profile-user-tooltip" }'
                )
            button(@click='deleteSchoolImage' type='button')
              simple-svg.dark-gray-hover-blue(
                :filepath='trashIcon'
                :width='"32px"'
                :height='"32px"'
                v-tooltip.bottom='{ content: $t("profile.deleteAvatar"), classes: "profile-user-tooltip" }'
              )
        .text-container.column.width-100
          .row.space-between.bottom
            .column.width-100
              label.text-normal.black.m-bottom-1
                | {{ $t('profile.schoolTab.edition.schoolName') }}
              input.edit.input.text-normal.dark-gray.bg-white(
                name='name'
                :value='schoolEdition.name'
                :maxlength='schoolNameMaxLength'
                @input='updateValue'
              )
          .row.location.middle
            .img-container.m-right-08
              simple-svg.blue-icon(:filepath='locationIcon' :width='"20px"' :height='"20px"')
            input.edit.text-normal.width-100(
              name='address'
              :value='schoolEdition.address'
              :maxlength='schoolAddressMaxLength'
              @input='updateValue'
            )
    .section.row.baseline
      h4.text-big.dark-gray.self-baseline.m-top-1
        | {{ $t('profile.schoolTab.bioLabel') }}
      .column.end
        textarea.edit.text-normal.dark-gray.text-area(:value='schoolEdition.bio' :maxlength='400'  name='bio' @input='updateValue')
        span.text-small.dark-gray.m-top-05
          | {{ $tc('profile.schoolTab.charactersLeft', charactersLeft)}}
    .section.row.relative
      h4.text-big.dark-gray
        | {{ $t('profile.schoolTab.team') }}
      avatars-list.m-left-1(
        :elementsList='schoolEdition.schoolTeam'
        :usersType='teamID'
      )
    .section.row
      h4.text-big.dark-gray.title
        | {{ $t('profile.schoolTab.levels') }}
      trial-disable-field(v-if='!isAdmin || !schoolEdition.impartedLevels')
        .column.m-left-1
          span.text-big.black.m-bottom-1(v-for='level in school.impartedLevels' :key='level')
            | {{ getEducationalLevelName(level) }}
      tag-input(
        v-else
        :value='schoolEdition.impartedLevels'
        :options='educationalLevelOptions'
        keyName='id'
        labelName='name'
        name='impartedLevels'
        @change='updateTagInput'
      )
    .section.row.email-main-container
      h4.text-big.dark-gray.self-baseline
        | {{ $t('profile.schoolTab.mail') }}
      .column.email-container
        input.edit.input.text-normal.dark-gray(
          name='email'
          :value='schoolEdition.email'
          :class='{ "input-error": emailError }'
          @input='updateValue'
        )
        .row.m4-top-1.middle(v-show='emailError')
          simple-svg.error-icon.red-icon.m4-right-1(:filepath='stopIcon' :width='"16px"' :height='"16px"')
          span.red
            | {{ $t('profile.schoolTab.emailError') }}
    .section.row.select-row-container
      h4.text-big.dark-gray.title
        | {{ $t('profile.schoolTab.students') }}
      //- TODO: Remove the -1 in the preselectedOption once the k-select choose by id and not by idx
      k-select.select.text-normal.dark-gray(
        :options='studentsRange'
        :preselectedOption='schoolEdition.studentsRange-1'
        name='studentsRange'
        withBorder
        edition
        @selectOption='updateSelect'
      )
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex'
import get from 'lodash/get'

import KSelect from '@/components/KSelect'
import AvatarsList from '@/components/Profile/AvatarsList'
import TagInput from '@/components/Inputs/TagInput'
import ProfileToast from '@/components/Profile/ProfileToast'

import locationIcon from '@/assets/ic_location.svg'
import editIcon from '@/assets/ic_edit.svg'
import trashIcon from '@/assets/ic_trash.svg'
import stopIcon from '@/assets/ic_stop.svg'
import defaultSchool from '@/assets/ic_school.svg'

import {
  firstElement,
  studentsRange,
  charactersLimit,
  managersID,
  teamID,
  userPrimaryRoles,
  maxFileSize,
  networkNameMaxLength,
  schoolNameMaxLength,
  cueMaxLength,
  schoolAddressMaxLength
} from '../../utils/constants'
import { toLowerCamelCase } from '../../utils/string'
import { getBase64, getExtension, isImage } from '../../utils/file'
import { isNumber } from '../../utils/validators'
import { createDiscardModalObject } from '../../utils/object'
import userRole from '../../mixins/userRole'
import TrialDisableField from './TrialDisableField'

export default {
  components: { KSelect, AvatarsList, TagInput, ProfileToast, TrialDisableField },
  mixins: [userRole],
  data() {
    return {
      locationIcon,
      editIcon,
      trashIcon,
      stopIcon,
      defaultSchool,
      networkNameMaxLength,
      schoolNameMaxLength,
      cueMaxLength,
      schoolAddressMaxLength,
      isNumber,
      studentsRange,
      managersID,
      teamID,
      userPrimaryRoles,
      toastDuration: 7000
    }
  },
  computed: {
    ...mapState({
      schoolGroup: state => state.profile.userProfile.schoolGroupVO,
      schoolEdition: state => get(state.profile, 'schoolGroupEdition.currentSchool') || {},
      educationalLevels: state => state.profile.educationalLevels,
      editionModified: state => state.profile.editionModified,
      errors: state => state.profile.errors
    }),
    ...mapGetters({
      schoolGroup: 'getSchoolGroup',
      school: 'getSchool'
    }),
    educationalLevelOptions() {
      return this.educationalLevels
        .map(level => ({ id: level.id, name: this.getEducationalLevelName(level.id) }))
        .filter(level => this.schoolGroup.educationalLevels.includes(level.id))
    },
    charactersLeft() {
      const currentAmountOfCharacters = this.schoolEdition.bio ? this.schoolEdition.bio.length : firstElement
      return charactersLimit - currentAmountOfCharacters
    },
    getSchoolsArray() {
      return this.schoolGroup.schools.map(school => ({ value: school.id, label: school.name }))
    },
    emailError() {
      return this.errors.type === 'email'
    }
  },
  async created() {
    this.showElement({ element: 'fullPageLoader' })
    await this.createSchoolEdition()
    this.hideElement({ element: 'fullPageLoader' })
  },
  methods: {
    ...mapActions([
      'createSchoolEdition',
      'updateSchoolEditionField',
      'uploadSchoolImage',
      'showElement',
      'hideElement',
      'setToastError',
      'hydrateSchoolInEdition'
    ]),
    getEducationalLevelName(id) {
      const level = this.educationalLevels.find(edLevel => edLevel.id === id)
      return level ? this.$t(`profile.educationalLevel.${toLowerCamelCase(level.name)}`) : ''
    },
    updateValue(e) {
      const value = e.target.value.trim()
      this.updateSchoolEditionField({ field: e.target.name, value })
    },
    updateSelect(name, value) {
      this.updateSchoolEditionField({ field: name, value })
    },
    updateTagInput(value, field) {
      this.updateSchoolEditionField({ field, value })
    },
    async handleSubmitImage(event) {
      this.imageSizeError = false
      const file = event.target.files[firstElement]
      const extension = getExtension(file.name)
      if (file.size > maxFileSize) {
        this.setToastError('imageSize')
        this.$notify({ group: 'profile-edition', duration: this.toastDuration })
      } else if (!isImage(extension)) {
        this.setToastError('imageType')
        this.$notify({ group: 'profile-edition', duration: this.toastDuration })
      } else {
        // TODO: Prevent uploading images that haven't been "saved"
        const base64 = await getBase64(file)
        this.uploadSchoolImage({ base64, extension, schoolId: this.schoolEdition.id })
      }
    },
    deleteSchoolImage() {
      this.updateSchoolEditionField({ field: 'avatar', value: '' })
    },
    async getSchoolInfoInEdition({ schoolId }) {
      this.showElement({ element: 'fullPageLoader' })
      await this.hydrateSchoolInEdition(schoolId)
      this.hideElement({ element: 'fullPageLoader' })
    },
    async getSchoolInfo(_, schoolId) {
      if (schoolId !== this.schoolEdition.id) {
        if (this.editionModified) {
          this.showElement(createDiscardModalObject(this.getSchoolInfoInEdition, { schoolId }))
        } else {
          await this.getSchoolInfoInEdition({ schoolId })
        }
      }
    }
  }
}
</script>

<style lang="scss" scoped>
@import '../../scss/variables/colors';

.section {
  align-items: center;
  display: grid;
  grid-template-columns: 190px 590px;
  padding: 48px 52px;

  &:not(:last-child) {
    border-bottom: 1px solid $light-gray;
  }

  .arrow {
    background: url('../../assets/ic_chevron-down.svg') no-repeat;
    background-color: $red;
    background-size: contain;
    content: '';
    height: 16px;
    position: absolute;
    right: 0;
    top: 0;
    width: 32px;
  }

  .title {
    max-width: 106px;
  }

  .img-container {
    align-items: center;
    max-height: 24px;
  }
}

.gray-section {
  background-color: $light-gray;
  height: 172px;
  margin-bottom: 76px;
  position: relative;
  width: 100%;

  .school-header {
    padding: 0 52px;
    position: relative;
    top: 48px;

    .school-image-container {
      background-color: $white;
      box-shadow: 0 2px 8px 0 $shadow-gray;
      height: 200px;
      max-width: 200px;
      object-fit: cover;
      width: 100%;
    }

    .school-image {
      min-height: 200px;
      padding: 32px;
      width: 100%;
    }

    .text-container {
      margin-left: 44px;

      .location {
        margin-top: 48px;
      }
    }
  }
}

.school-section {
  height: 137px;
}

.img-buttons-container {
  background-color: $shadow-white;
  height: 100%;
  opacity: 0;
  top: 0;

  &:hover {
    opacity: 1;
  }
}

.cue {
  margin-left: 25px;
  max-width: 160px;
  width: 100%;
}

.edit {
  background: transparent;
  border: 1px solid $gray;
  border-radius: 6px;
  box-sizing: border-box;
  padding: 12px;
}

.text-area {
  height: 180px;
  resize: none;
  width: 100%;
}

%inputs-width {
  max-width: 344px;
  width: 100%;
}

.hidden-input {
  width: 0.1px;
  height: 0.1px;
  position: absolute;
  overflow: hidden;
}

.input {
  @extend %inputs-width;
  max-height: 40px;
}

.select {
  @extend %inputs-width;
}

.select-row-container {
  height: 300px;
  align-items: flex-start;
}

.input-error {
  border: 1px solid $red;
}

.email-main-container {
  padding-bottom: 28px;
}

.email-container {
  height: 60px;
}

.error-icon {
  height: 16px;
  width: 16px;
}

.bg-white {
  background-color: $white;
}
</style>
